import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, BookOpen, ArrowRight, AlertCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QRCodePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [context, setContext] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const resolveCode = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/r/${code}`);
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || "Invalid QR code");
        }
        const data = await response.json();
        setContext(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      resolveCode();
    }
  }, [code]);

  // Once we have context and auth is resolved, handle redirect
  useEffect(() => {
    if (!loading && !authLoading && context) {
      // Store context in sessionStorage for the chat page to pick up
      sessionStorage.setItem("qr_context", JSON.stringify(context));
      
      if (isAuthenticated) {
        // Go directly to chat with context
        navigate(`/dashboard/chat?context=qr&series=${context.series.slug}&book=${context.book.number}&day=${context.day.number}`, { replace: true });
      }
      // If not authenticated, show the landing page with sign-in option
    }
  }, [loading, authLoading, context, isAuthenticated, navigate]);

  const handleContinue = () => {
    if (context) {
      sessionStorage.setItem("qr_context", JSON.stringify(context));
      navigate(`/login?redirect=${encodeURIComponent(`/dashboard/chat?context=qr&series=${context.series.slug}&book=${context.book.number}&day=${context.day.number}`)}`);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-red-500 animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#2a2a2a] rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Invalid Link</h1>
          <p className="text-white/60 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  // Show landing page for non-authenticated users
  if (!isAuthenticated && context) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 rounded-full mb-4">
              <BookOpen className="w-4 h-4 text-red-500" />
              <span className="text-red-500 text-sm font-medium">From your book</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Day {context.day.number}: {context.day.title}
            </h1>
            <p className="text-white/60">
              {context.book.title} &middot; {context.series.name}
            </p>
          </div>

          {/* Context Card */}
          <div className="bg-[#2a2a2a] rounded-xl p-6 mb-6">
            <div className="mb-4">
              <h2 className="text-sm font-medium text-white/40 uppercase tracking-wider mb-2">
                Today's Theme
              </h2>
              <p className="text-white">{context.day.theme}</p>
            </div>
            
            <div className="mb-4">
              <h2 className="text-sm font-medium text-white/40 uppercase tracking-wider mb-2">
                Key Takeaway
              </h2>
              <p className="text-white/80 italic">"{context.day.keyTakeaway}"</p>
            </div>

            <div className="border-t border-white/10 pt-4 mt-4">
              <h2 className="text-sm font-medium text-white/40 uppercase tracking-wider mb-2">
                Reflection Prompt
              </h2>
              <p className="text-white/80">{context.reflectionPrompt}</p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-red-600 to-purple-600 rounded-xl p-6 text-center">
            <h3 className="text-lg font-semibold text-white mb-2">
              Ready to reflect on this?
            </h3>
            <p className="text-white/80 text-sm mb-4">
              Your AI Companion remembers your context and helps you apply what you've learned.
            </p>
            <button
              onClick={handleContinue}
              data-testid="qr-continue-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-red-600 font-semibold rounded-lg hover:bg-white/90 transition-colors"
            >
              Continue to Companion
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Footer */}
          <p className="text-center text-white/40 text-sm mt-6">
            New here? You'll get a free trial to explore your Companion.
          </p>
        </div>
      </div>
    );
  }

  // Fallback (shouldn't reach here normally)
  return (
    <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
    </div>
  );
};

export default QRCodePage;
